import { AnimationPlayer } from '@angular/animations';
import { Util } from '@libs/utilities/util';

import { EnumNgEventName, NG_EVENT_TRIGGERS } from './event-animation.data';
import { TypeNgAnimationParams, INgAnimationTiming } from './event-animation.interface';


/**------------------------------------------------------
 * Abstract Animation
 * ------------------
 * > Parent to all animation service implementations.
 */
export abstract class AbstractNgAnimationService {

	//** Configurations */
	protected readonly DEFAULT_TIMING: INgAnimationTiming = {
		duration	: 250,
		delay		: 0
	};

	//** Helper Variables */
	protected animationPlayer: AnimationPlayer = null!;


	/**------------------------------------------------------
	 * Methods to Implement
	 */
	abstract setupAnimation(params: TypeNgAnimationParams): void;
	protected abstract verifyCustomParameters(params: TypeNgAnimationParams): TypeNgAnimationParams;


	/**------------------------------------------------------
	 * Helper Functions
	 */
	protected verifyParameters(params: TypeNgAnimationParams, defaultTimingOptions: INgAnimationTiming = this.DEFAULT_TIMING): TypeNgAnimationParams {

		//0 - check the trigger element
		if (!params?.triggerElement) throw new Error(`AbstractAnimationService => verifyParameters => FATAL ERROR: The trigger element is required to access the html element for the animations (triggerElement: ${params?.triggerElement})`);

		//1 - check the target element (if no target was defined, we animate the element which is also the trigger)
		if (params?.targetElement && !(params?.targetElement instanceof HTMLElement)) throw new Error(`AbstractAnimationService => verifyParameters => FATAL ERROR: The target element needs to be of type HTMLElement. If the target is a component, please wrap a HTMLElement around the component (targetElement: ${params?.targetElement})`);
		if (!params?.targetElement) params.targetElement = params.triggerElement;

		//2 - check the event that we will subscribe to
		if (!Util.Enum.isValid(EnumNgEventName, params?.event)) throw new Error(`AbstractAnimationService => verifyParameters => FATAL ERROR: The event for triggering the animation is required, and has to be valid (provided event: ${params?.event} / valid events: ${Util.Enum.values(EnumNgEventName).join(', ')})`);
		if (!NG_EVENT_TRIGGERS[params?.event]) 				  throw new Error(`AbstractAnimationService => verifyParameters => FATAL ERROR: The event of "${params?.event}" was not defined in the EVENT_TRIGGERS configuration, please check the effects configurations (configured are: ${Object.keys(NG_EVENT_TRIGGERS).join(', ')})`);

		//3 - purify the timing parameters
		// > WORKAROUND: need to check for string because if a parameter is not
		// > passed in angular the value will be an empty string not undefined/null
		if (Util.String.isString(params?.timing)) params.timing = null!;
		params.timing = Util.Function.assignOptions<INgAnimationTiming>(defaultTimingOptions, params.timing);

		//4 - check the timing parameters
		if (params.timing.delay! 	 < 0) throw new Error(`AbstractAnimationService => verifyParameters => FATAL ERROR: The delay for the animation can not be smaller then 0 (provided value: ${params.timing.delay})`);
		if (params.timing.duration! <= 0) throw new Error(`AbstractAnimationService => verifyParameters => FATAL ERROR: The duration for the animation can not be smaller or equal to 0 (provided value: ${params.timing.duration})`);

		//5 - return the cleaned parameters
		params = this.verifyCustomParameters(params);
		return params;
	}
}
